import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronRight, Search, ExternalLink, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { StateGrid } from "@/components/explore/StateGrid";
import { SpeciesGrid } from "@/components/explore/SpeciesGrid";
import { HuntResults } from "@/components/explore/HuntResults";
import { HuntDetailDrawer } from "@/components/explore/HuntDetailDrawer";
import { SYSTEM_STYLES } from "@/lib/stateMeta";

const STEP_LABELS = [
  { q: "Where do you want to hunt?", hint: "Pick a state" },
  { q: "What do you want to hunt?", hint: "Pick a species" },
  { q: "Refine your hunts", hint: "Choose your method & filters" },
];

export default function HuntExplorer() {
  const navigate = useNavigate();
  const [states, setStates] = useState(null);
  const [query, setQuery] = useState("");
  const [onlyCovered, setOnlyCovered] = useState(false);
  const [state, setState] = useState(null);
  const [speciesData, setSpeciesData] = useState(null);
  const [species, setSpecies] = useState(null);
  const [points, setPoints] = useState(0);
  const [openHunt, setOpenHunt] = useState(null);

  useEffect(() => { api.exploreStates().then(setStates).catch(() => setStates([])); }, []);

  const step = species ? 2 : state ? 1 : 0;

  const pickState = (s) => navigate(`/states/${encodeURIComponent(s.state)}`);
  const reset = () => { setState(null); setSpecies(null); setSpeciesData(null); };

  const filteredStates = (states || []).filter((s) =>
    (!onlyCovered || s.has_data) && (!query || s.state.toLowerCase().includes(query.toLowerCase())));

  return (
    <div className="bg-paper min-h-screen">
      <SiteHeader />
      {/* Header */}
      <section className="bg-forest text-bone px-5 sm:px-10 pt-14 pb-10 grain" data-testid="explorer-header">
        <div className="max-w-[1400px] mx-auto">
          <span className="hr-overline text-blaze inline-flex items-center gap-2"><Compass size={14} /> Draw Odds Explorer</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight mt-3">{STEP_LABELS[step].q}</h1>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mt-5 text-sm flex-wrap" data-testid="explorer-breadcrumb">
            <button onClick={reset} className={`${step === 0 ? "text-blaze" : "text-bone/70 hover:text-bone"}`} data-testid="crumb-states">States</button>
            {state && <><ChevronRight size={14} className="text-bone/40" /><button onClick={() => setSpecies(null)} className={`${step === 1 ? "text-blaze" : "text-bone/70 hover:text-bone"}`} data-testid="crumb-state">{state.state}</button></>}
            {species && <><ChevronRight size={14} className="text-bone/40" /><span className="text-blaze" data-testid="crumb-species">{species.species}</span></>}
          </div>
        </div>
      </section>

      <main className="px-5 sm:px-10 py-10 max-w-[1400px] mx-auto">
        {states === null ? (
          <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        ) : step === 0 ? (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="relative w-full sm:w-72">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search states…" className="w-full border border-saddle/25 bg-white pl-9 pr-3 py-2.5 text-sm outline-none focus:border-forest" data-testid="state-search" />
              </div>
              <label className="flex items-center gap-2 text-sm text-charcoal/70">
                <input type="checkbox" checked={onlyCovered} onChange={(e) => setOnlyCovered(e.target.checked)} data-testid="only-covered" />
                Show only states with verified data
              </label>
            </div>
            <StateGrid states={filteredStates} onPick={pickState} />
          </>
        ) : step === 1 ? (
          speciesData === null ? (
            <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
          ) : speciesData.species.length === 0 ? (
            <CoveragePanel state={state} source={speciesData.official_source} />
          ) : (
            <>
              <div className={`inline-block text-xs font-semibold uppercase tracking-wide px-2 py-1 mb-6 ${SYSTEM_STYLES[state.system]}`} data-testid="state-system-badge">
                {state.system_label} — {speciesData.system?.system_desc}
              </div>
              <SpeciesGrid species={speciesData.species} onPick={setSpecies} />
            </>
          )
        ) : (
          <HuntResults state={state.state} species={species.species} weapons={species.weapons} points={points} setPoints={setPoints} onOpen={setOpenHunt} />
        )}
      </main>

      <HuntDetailDrawer huntId={openHunt} points={points} onClose={() => setOpenHunt(null)} />
      <Footer />
    </div>
  );
}

function CoveragePanel({ state, source }) {
  return (
    <div className="max-w-2xl mx-auto text-center py-16" data-testid="coverage-panel">
      <div className={`inline-block text-xs font-semibold uppercase tracking-wide px-2 py-1 mb-5 ${SYSTEM_STYLES[state.system]}`}>{state.system_label}</div>
      <h2 className="font-serif text-3xl text-forest">Verified {state.state} draw data is coming soon</h2>
      <p className="text-charcoal/65 mt-4">Our HomeRange Data system is actively verifying official {state.agency} draw results and point statistics. We only publish numbers confirmed against official state sources — so this state isn&apos;t live yet.</p>
      {source && (
        <a href={source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-7 bg-forest text-bone px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-blaze transition-colors" data-testid="coverage-official-link">
          Visit official {state.state} source <ExternalLink size={15} />
        </a>
      )}
    </div>
  );
}
